import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { admin } from '../../../services/api';
import { parseReconciliation, type ReconciliationView } from '../../../utils/usageContract';
import './UsageBilling.css';

/**
 * Сверка ML-леджера с базой (AIT-259).
 *
 * Отчёт отвечает на один вопрос: сходится ли то, что насчитал ML, с тем, что
 * записано в `usage_records`. Строки `ml-shadow` исключены из всех остальных
 * отчётов именно ради этой сверки — здесь они сравниваются намеренно.
 *
 * Ответ приходит в трёх разных формах, и две из них — не ошибка, а состояние
 * механизма. Их важно развести: «выключено» чинится переменной окружения,
 * «ещё не считалось» пройдёт само в течение суток.
 */

const fmtInt = (v: number): string => v.toLocaleString('en-US');

function fmtMoment(value: string | null): string {
  if (!value) return '—';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleString('en-GB', { dateStyle: 'medium', timeStyle: 'short' });
}

interface Props {
  onError?: (message: string | null) => void;
  /** Меняется, когда в шапке раздела нажали Refresh. */
  refreshToken?: number;
}

const UsageReconciliationPanel: React.FC<Props> = ({ onError, refreshToken = 0 }) => {
  const [view, setView] = useState<ReconciliationView | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    onError?.(null);
    try {
      const resp = await admin.getUsageReconciliation();
      // Ручка кладёт отчёт в `data`, но на всякий случай разбираем и сам конверт:
      // состояние «unavailable» может прийти в любом из двух мест.
      setView(parseReconciliation(resp?.data ?? resp));
    } catch (e) {
      const message =
        (e as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error?.message ||
        (e as Error)?.message ||
        'Failed to load the reconciliation report.';
      setView(null);
      setError(message);
      onError?.(message);
    } finally {
      setLoading(false);
    }
  }, [onError]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (!refreshToken) return;
    void load();
  }, [refreshToken, load]);

  const worst = useMemo(() => {
    if (!view || view.state !== 'report') return 0;
    return view.discrepancies.reduce((max, d) => Math.max(max, Math.abs(d.discrepancy_pct)), 0);
  }, [view]);

  if (error) {
    return (
      <div className="ub">
        <div className="ub-error" role="alert">
          <i className="fas fa-triangle-exclamation" aria-hidden="true" />
          <div>{error}</div>
          <button type="button" className="ub-btn" onClick={() => void load()}>
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (loading && !view) {
    return (
      <div className="ub">
        <div className="ub-state">
          <span className="ub-skeleton" style={{ width: 34, height: 34, borderRadius: 10 }} />
          <div style={{ flex: 1 }}>
            <span className="ub-skeleton" style={{ display: 'block', width: '38%', height: 14, marginBottom: 8 }} />
            <span className="ub-skeleton" style={{ display: 'block', width: '70%' }} />
          </div>
        </div>
      </div>
    );
  }

  if (!view || view.state === 'unavailable') {
    return (
      <div className="ub">
        <div className="ub-state ub-state--warn">
          <span className="ub-state__icon">
            <i className="fas fa-plug-circle-xmark" aria-hidden="true" />
          </span>
          <div>
            <div className="ub-state__title">Reconciler is off</div>
            <div className="ub-state__text">
              The backend is not running the comparison pass, so there is nothing to show — this is a switch, not a
              failure. It is enabled with <code>METERING_RECONCILER_ENABLED=true</code> on the service; on dev it is
              currently off by default.
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (view.state === 'not_run_yet') {
    return (
      <div className="ub">
        <div className="ub-state">
          <span className="ub-state__icon">
            <i className="fas fa-hourglass-half" aria-hidden="true" />
          </span>
          <div>
            <div className="ub-state__title">First pass has not run yet</div>
            <div className="ub-state__text">
              The reconciler is enabled but has not completed a cycle. The pass runs once every 24 hours, and the report
              is held in the process memory — a backend restart clears it and the wait starts over.
            </div>
          </div>
        </div>
      </div>
    );
  }

  const clean = view.discrepancies.length === 0;

  return (
    <div className="ub">
      <div className={`ub-state ${clean ? 'ub-state--ok' : 'ub-state--bad'}`}>
        <span className="ub-state__icon">
          <i className={`fas ${clean ? 'fa-circle-check' : 'fa-scale-unbalanced'}`} aria-hidden="true" />
        </span>
        <div>
          <div className="ub-state__title">
            {clean
              ? 'ML and the database agree'
              : `${view.discrepancies.length} run(s) disagree by more than 2%`}
          </div>
          <div className="ub-state__text">
            {`Checked ${fmtInt(view.runsChecked)} ML run(s) over ${fmtMoment(view.from)} → ${fmtMoment(view.to)}. `}
            {clean
              ? 'Every run matched within the 2% threshold, so billed totals can be trusted for this window.'
              : `Worst gap on this pass: ${worst.toFixed(1)}%. Only runs above the 2% threshold are listed.`}
          </div>
          {view.error ? (
            <div className="ub-state__text" style={{ marginTop: 6, color: '#B85400' }}>
              {`Reconciler reported an error: ${view.error}`}
            </div>
          ) : null}
        </div>
      </div>

      <div className="ub-kpis">
        <div className="ub-kpi">
          <span className="ub-kpi__label">Generated</span>
          <span className="ub-kpi__value" style={{ fontSize: 15 }}>
            {fmtMoment(view.generatedAt)}
          </span>
          <span className="ub-kpi__hint">kept in memory, not in the database</span>
        </div>
        <div className="ub-kpi">
          <span className="ub-kpi__label">Runs checked</span>
          <span className="ub-kpi__value">{fmtInt(view.runsChecked)}</span>
          <span className="ub-kpi__hint">ML runs in the window</span>
        </div>
        <div className="ub-kpi">
          <span className="ub-kpi__label">Discrepancies</span>
          <span className="ub-kpi__value" style={{ color: clean ? '#0A2A47' : '#B85400' }}>
            {fmtInt(view.discrepancies.length)}
          </span>
          <span className="ub-kpi__hint">above the 2% threshold</span>
        </div>
        <div className="ub-kpi">
          <span className="ub-kpi__label">Worst gap</span>
          <span className="ub-kpi__value" style={{ color: clean ? '#0A2A47' : '#B85400' }}>
            {clean ? '0%' : `${worst.toFixed(1)}%`}
          </span>
          <span className="ub-kpi__hint">ML versus usage_records</span>
        </div>
      </div>

      {clean ? null : (
        <div className="ub-table-wrap">
          <div className="ub-table-scroll">
            <table className="ub-table">
              <thead>
                <tr>
                  <th>Request</th>
                  <th className="ub-num">ML tokens</th>
                  <th className="ub-num">DB tokens</th>
                  <th className="ub-num">Difference</th>
                  <th className="ub-num">Gap</th>
                </tr>
              </thead>
              <tbody>
                {view.discrepancies.map((d) => {
                  const diff = d.ml_total_tokens - d.db_total_tokens;
                  return (
                    <tr key={d.request_id}>
                      <td>
                        <span className="ub-req">{d.request_id}</span>
                      </td>
                      <td className="ub-num">{fmtInt(d.ml_total_tokens)}</td>
                      <td className="ub-num">{fmtInt(d.db_total_tokens)}</td>
                      <td className="ub-num">{`${diff > 0 ? '+' : ''}${fmtInt(diff)}`}</td>
                      <td className="ub-num">
                        <span className="ub-delta">{`${d.discrepancy_pct.toFixed(1)}%`}</span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default UsageReconciliationPanel;
